import { QuickPriceCalculatorTypeLister } from "../pricelist/quickprice_lister";
import { QuickPriceCalculatorTypeWidget } from "../pricelist/quickprice_widget";
import { NO_PRICE } from "../pricelist/quickprice";
import { roundPrice } from "../utils/round";
import { hasHotelActiveContract, ICompanyProps, IHotelProps, IHotelWithContractsProps, IOfferProps } from "./cancellation";

export interface ICommissionRate {
  commissionBase: 'NET' | 'GROSS';
  commissionPercentage: number;
}

export interface ICommission {
  commissionBase: 'NET' | 'GROSS';
  commissionPercentage: number;
  commissionBaseAmount: number;
  commissionAmountNet: number;
  commissionAmountGross: number;
}

export const COMMISSIONABLE_LOOKUP_CODES: string[] = [
  'TAGESPAUSCHALE',
  'HALBTAGESPAUSCHALE',
  'SEMINARPAUSCHALE_DZ',
  'SEMINARPAUSCHALE_EZ',
  'ABREISEPAUSCHALE',
  'VORTAGSANREISE_EZ',
  'VORTAGSANREISE_DZ',
  'ADDON_COFFEE',
  'ADDON_LUNCH',
  'ADDON_COFFEEBREAK',
  'ADDON_DINNER',
  'ROOM_EZ',
  'ROOM_DZ',
  'VOLLPENSIONSPAUSCHALE_EZ',
  'VOLLPENSIONSPAUSCHALE_DZ',
  'SEMINARROOM_FULLDAY',
  'SEMINARROOM_SEMIDAY',
  'BEAMER',
  'FLIPCHART',
  'LEINWAND',
  'PINNWAND',
  'TV',
  'CAM',
  'CD',
  'MODERATION',
  'GROUPROOM'
];

export const COMMISSIONABLE_LOOKUP_CODES_NO_ZERO_TAX: string[] = [
  'SEMINARPAUSCHALE_DZ',
  'SEMINARPAUSCHALE_EZ',
  'VORTAGSANREISE_EZ',
  'VORTAGSANREISE_DZ',
  'ROOM_EZ',
  'ROOM_DZ',
  'VOLLPENSIONSPAUSCHALE_EZ',
  'VOLLPENSIONSPAUSCHALE_DZ',
];

export interface ICommissionableOfferVersion {
  offer: {
    source: 'WIDGET' | 'LISTER';
    process: 'SEM' | 'CLASSIC' | 'PREMIUM';
  },
  agreedCancellationWaiveNet?: number | null;
  manualCancellationWaiveNet?: number | null;
  contentBlocks: {
    type: string; //'LINE_ITEMS' | 'CANCELLATION_ITEMS';
    lineItems: {
      source: string; //'OFFER' | 'EXTRA' | 'MANUAL' | 'REQUESTED';
      sku?: string | null;
      count: number;
      priceItem: number;
      priceNet: number;
      priceGross: number;
      invoice?: {
        commission?: boolean | null;
      } | null;
      components: {
        price: number;
        taxType: {
          lookupCode?: string | null;
        }
      }[]
    }[]
  }[]
}

const _skusForLookupCodes = (quickprice: QuickPriceCalculatorTypeLister | QuickPriceCalculatorTypeWidget, lookupCodes: string[]) => [
  ...(quickprice.allProductsDb || []).filter(p => p.lookupCode && lookupCodes.includes(p.lookupCode)).map(p => p.sku),
  ...(quickprice.allBundlesDb || []).filter(p => p.lookupCode && lookupCodes.includes(p.lookupCode)).map(p => p.sku),
  ...(quickprice.allFacilitiesDb || []).filter(p => p.lookupCode && lookupCodes.includes(p.lookupCode)).map(p => p.sku),
];

const _readCommissionableProductsForOffer = (
  version: ICommissionableOfferVersion,
  quickprice: QuickPriceCalculatorTypeWidget | QuickPriceCalculatorTypeLister,
) => {
  const ra: { sku: string | null | undefined; count: number; priceNet: number; priceGross: number, isCancellationFee: boolean }[] = [];

  const commissionableSkus = _skusForLookupCodes(quickprice, COMMISSIONABLE_LOOKUP_CODES);
  const commissionableSkusNoZeroTax = _skusForLookupCodes(quickprice, COMMISSIONABLE_LOOKUP_CODES_NO_ZERO_TAX);

  for (const cb of version.contentBlocks) {
    for (const li of cb.lineItems) {
      if (cb.type === 'LINE_ITEMS') {
        if (version.offer.process === 'PREMIUM') {
          if (li.source === 'OFFER' || li.source === 'MANUAL') {
            if (!li.sku) continue;
            if (!commissionableSkus.includes(li.sku)) continue;
            if (li.count <= 0) continue;
            if (li.priceItem === 0) continue;
          } else if (li.source === 'EXTRA') {
            if (!li.invoice?.commission) continue;
            if (li.count <= 0) continue;
            if (li.priceItem === 0) continue;
          } else {
            continue;
          }
        } else {
          if (li.count <= 0) continue;
          if (li.priceItem <= 0) continue;
        }
      } else if (cb.type === 'CANCELLATION_ITEMS') {
        if (li.count <= 0) continue;
        if (li.priceItem <= 0) continue;
      } else {
        continue;
      }

      if (li.priceNet === NO_PRICE || li.priceGross === NO_PRICE) continue;

      let commissionablePriceNet = li.priceNet;
      let commissionablePriceGross = li.priceGross;
      if (version.offer.process === 'PREMIUM' && li.sku && commissionableSkusNoZeroTax.includes(li.sku)) {
        const zeroTaxComponent = li.components.find(c => c.taxType.lookupCode === 'NOTAX');
        if (zeroTaxComponent) {
          commissionablePriceNet = li.priceNet - zeroTaxComponent.price * li.count;
          commissionablePriceGross = li.priceGross - zeroTaxComponent.price * li.count;
        }
      }

      const isCancellationFee = cb.type === 'CANCELLATION_ITEMS';

      const existingRa = ra.find(r => r.sku === li.sku && r.isCancellationFee === isCancellationFee);
      if (existingRa) {
        existingRa.count += li.count;
        existingRa.priceNet += commissionablePriceNet;
        existingRa.priceGross += commissionablePriceGross;
      } else {
        ra.push({
          sku: li.sku,
          count: li.count,
          priceNet: commissionablePriceNet,
          priceGross: commissionablePriceGross,
          isCancellationFee: isCancellationFee,
        });
      }
    }
  }

  const totalCommissionablePriceNet = ra.filter(r => !r.isCancellationFee).reduce((acc, r) => acc + r.priceNet, 0);
  const totalCommissionablePriceGross = ra.filter(r => !r.isCancellationFee).reduce((acc, r) => acc + r.priceGross, 0);

  const totalCancellationFeeNet = ra.filter(r => r.isCancellationFee).reduce((acc, r) => acc + r.priceNet, 0);
  const realCancellationFeeNet = Math.max(0, totalCancellationFeeNet - (version.agreedCancellationWaiveNet || 0) - (version.manualCancellationWaiveNet || 0));

  return {
    totalCommissionablePriceNet: totalCommissionablePriceNet + realCancellationFeeNet,
    totalCommissionablePriceGross: totalCommissionablePriceGross + realCancellationFeeNet, // cancellation fees ohne steuer
    itemsTotal: ra,
  };
};

export const calculateCommissionForOffer = (
  version: ICommissionableOfferVersion,
  commissionRate: ICommissionRate,
  quickprice: QuickPriceCalculatorTypeWidget | QuickPriceCalculatorTypeLister,
): ICommission => {
  const r: ICommission = {
    commissionBase: commissionRate.commissionBase,
    commissionPercentage: commissionRate.commissionPercentage,
    commissionBaseAmount: 0,
    commissionAmountNet: 0,
    commissionAmountGross: 0,
  };

  const commissionableProducts = _readCommissionableProductsForOffer(version, quickprice);
  r.commissionBaseAmount =
    r.commissionBase === 'NET' ? commissionableProducts.totalCommissionablePriceNet : commissionableProducts.totalCommissionablePriceGross;
  r.commissionAmountNet = roundPrice(r.commissionBaseAmount * r.commissionPercentage);
  //TODO: Steuer berechnen
  r.commissionAmountGross = r.commissionAmountNet * 1.2;

  return r;
};

export interface IHotelCommissionRate {
  customerType: 'CLASSIC' | 'PREMIUM';
  commissionBase: 'NET' | 'GROSS';
  commissionPercentage: number;
  validFrom: Date;
  remark?: string | null;
}

export interface IHotelWithCommissionRatesProps extends IHotelWithContractsProps {
  commissionRates?: IHotelCommissionRate[];
}

export const getDefaultCommissionRate = (
  hotel: IHotelWithCommissionRatesProps, 
  company: ICompanyProps | null | undefined, 
  offerDate: Date
): ICommissionRate => {
  const customerType = (company && company.isPremium) ? 'PREMIUM' : 'CLASSIC';
  
  // 1. Check for manual hotel-specific commission rate
  // Find the most recent rate that is valid for the offer date
  if (hotel.commissionRates && hotel.commissionRates.length > 0) {
    const validRates = hotel.commissionRates
      .filter(rate => 
        rate.customerType === customerType &&
        rate.validFrom <= offerDate
      )
      .sort((a, b) => b.validFrom.getTime() - a.validFrom.getTime());
    
    if (validRates.length > 0) {
      const manualRate = validRates[0];
      return {
        commissionBase: manualRate.commissionBase,
        commissionPercentage: manualRate.commissionPercentage,
      };
    }
  }
  
  // 2. Fall back to contract-based rates 
  if (hasHotelActiveContract(hotel, 'SYMPOSION_HOTEL_CONTRACT', offerDate) || 
      hasHotelActiveContract(hotel, 'FRIEND_HOTEL_CONTRACT', offerDate)) {
    return {
      commissionBase: 'NET',
      commissionPercentage: 0,
    };
  }
  if (hasHotelActiveContract(hotel, 'PARTNER_HOTEL_CONTRACT', offerDate) ||
      hasHotelActiveContract(hotel, 'MEETING_PACKAGE_IMPORT', offerDate)) {
    if (company && company.isPremium) {
      return {
        commissionBase: 'NET',
        commissionPercentage: 0.1,
      };
    } else {
      return {
        commissionBase: 'GROSS',
        commissionPercentage: 0.1,
      };
    }
  }

  // 3. Default rates
  if (company && company.isPremium) {
    return {
      commissionBase: 'GROSS',
      commissionPercentage: 0.12,
    };
  } else {
    return {
      commissionBase: 'GROSS',
      commissionPercentage: 0.12,
    };
  }
};